// src/components/dwh/DwhSetupModal.tsx
//
// Unified DWH setup flow — embeddable via <DwhSetupContent /> or
// standalone via <DwhSetupModal />.
//
// 3-step wizard: Method → Setup → Connect

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CopyButton,
  Divider,
  Grid,
  Group,
  List,
  Loader,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconCheck,
  IconClock,
  IconCloud,
  IconCopy,
  IconDatabase,
  IconPlug,
  IconPlugConnected,
  IconServer,
  IconSettings,
  IconSnowflake,
} from "@tabler/icons-react";

import {
  connectDwh,
  getDwhStatus,
  getDwhRegions,
  provisionDwh,
  regionsToSelectData,
  validateDwh,
  type DwhStatus,
  type DwhRegions,
} from "@/api/dataWarehouse";
import {
  buildSnowflakeConnectPayload,
  connectSnowflakeDwh,
  getSnowflakeDwhStatus,
  validateSnowflakeDwh,
  type SnowflakeConnectFormValues,
  type SnowflakeDwhConnectResult,
  type SnowflakeDwhStatus,
} from "@/api/dataWarehouseSnowflake";
import {
  EMPTY_SNOWFLAKE_FORM,
  SnowflakeConnectInstructions,
  SnowflakeSetupForm,
} from "./SnowflakeSetup";
import { snowflakeFormErrors } from "./snowflakeValidation";
import { ApiError } from "@/api/client";
import { useDwhProvisioning } from "@/context/DwhProvisioningContext";
import { useBranding } from "@/context/BrandingContext";
import { AppModal } from "@/components/modals/AppModal";

// ── Validation ──────────────────────────────────────────────────

const PROJECT_NAME_RE = /^[a-z][a-z0-9-]*$/;
// Fallbacks used only until the backend status (source of truth) loads. The
// backend resolves the real suffix + length budget per environment.
const DEFAULT_PROJECT_SUFFIX = "-vrt-dwh";
const DEFAULT_MAX_PROJECT_NAME_LENGTH = 22;

// Known suffixes, longest-first so "-stg-vrt-dwh" is matched before "-vrt-dwh"
// (a staging id also ends in "-vrt-dwh" — checking the short one first would
// leave a stray "-stg").
const KNOWN_SUFFIXES = ["-stg-vrt-dwh", "-vrt-dwh", "-dev-dwh"];

function validateProjectName(name: string, maxLength: number): string | null {
  if (!name) return "Project name is required";
  if (!PROJECT_NAME_RE.test(name))
    return "Must start with a letter and contain only lowercase letters, digits, and hyphens";
  if (name.endsWith("-")) return "Must not end with a hyphen";
  if (name.length > maxLength)
    return `Too long (max ${maxLength} characters)`;
  return null;
}

function stripSuffix(projectId: string, suffix?: string | null): string {
  const candidates = suffix ? [suffix, ...KNOWN_SUFFIXES] : KNOWN_SUFFIXES;
  for (const s of candidates) {
    if (projectId.endsWith(s)) {
      return projectId.slice(0, -s.length);
    }
  }
  return projectId;
}

// ── Steps ───────────────────────────────────────────────────────

type SetupStep = "method" | "setup" | "connect";
/** "byo" = bring-your-own BigQuery; "snowflake" = bring-your-own Snowflake. */
type DwhChoice = "managed" | "byo" | "snowflake" | null;

const STEPS: { key: SetupStep; label: string; icon: React.ReactNode }[] = [
  { key: "method", label: "Method", icon: <IconServer size={16} /> },
  { key: "setup", label: "Setup", icon: <IconSettings size={16} /> },
  { key: "connect", label: "Connect", icon: <IconPlug size={16} /> },
];

const STEP_ORDER: SetupStep[] = ["method", "setup", "connect"];

const CHOICE_LABELS: Record<string, string> = {
  managed: "Verity Managed",
  byo: "Bring Your Own",
  snowflake: "Snowflake",
};

// ── Types ───────────────────────────────────────────────────────

export type DwhSetupContentProps = {
  /** Called when DWH becomes ACTIVE — the caller decides what to do next */
  onReady?: () => void;
  /** Contextual label, e.g. "Facebook Ads". Adjusts heading & success text. */
  contextLabel?: string;
  /** Close handler — renders a close button in the header when provided */
  onClose?: () => void;
  /**
   * Lays out the three regions itself instead of the built-in layout (title
   * row, stepper, scrolling body, footer). DwhSetupModal uses it to put them
   * in AppModal's pinned row, body and footer.
   */
  frame?: (parts: DwhSetupFrameParts) => ReactNode;
};

export type DwhSetupFrameParts = {
  /** `null` while the status is loading. */
  stepper: ReactNode;
  body: ReactNode;
  /** `null` when the current step has no navigation (method, success, loading). */
  footer: ReactNode;
};

export type DwhSetupModalProps = {
  opened: boolean;
  onClose: () => void;
  zIndex?: number;
} & DwhSetupContentProps;

// ── Stepper UI ──────────────────────────────────────────────────

function StepIndicator({
  step,
  success,
  subtitles,
}: {
  step: SetupStep;
  success: boolean;
  /** Small label under each step, e.g. { method: "Bring Your Own", setup: "my-project" } */
  subtitles?: Partial<Record<SetupStep, string | null>>;
}) {
  const currentIndex = STEP_ORDER.indexOf(step);

  return (
    <Group gap={0} wrap="nowrap">
      {STEPS.map((s, i) => {
        const isActive = i === currentIndex && !success;
        const isCompleted = i < currentIndex || success;
        const isLast = i === STEPS.length - 1;
        const subtitle = subtitles?.[s.key];

        return (
          <Group key={s.key} gap={0} wrap="nowrap" style={{ flex: 1 }}>
            <Group gap="xs" wrap="nowrap">
              <ThemeIcon
                size={28}
                radius="xl"
                variant={isCompleted ? "filled" : isActive ? "filled" : "light"}
                color={isCompleted ? "green" : isActive ? "blue" : "gray"}
              >
                {isCompleted ? (
                  <IconCheck size={14} />
                ) : (
                  <Text size="xs" fw={600}>
                    {i + 1}
                  </Text>
                )}
              </ThemeIcon>
              <Stack gap={0}>
                <Text
                  size="sm"
                  fw={isActive ? 600 : 400}
                  c={isActive ? undefined : isCompleted ? "green" : "dimmed"}
                  lh={1.3}
                >
                  {s.label}
                </Text>
                {subtitle && (
                  <Text size="xs" c="dimmed" lh={1.3} lineClamp={1}>
                    {subtitle}
                  </Text>
                )}
              </Stack>
            </Group>

            {!isLast && (
              <Box
                mx="sm"
                style={{
                  flex: 1,
                  height: 1,
                  backgroundColor: isCompleted
                    ? "var(--mantine-color-green-4)"
                    : "var(--app-border, var(--mantine-color-gray-2))",
                }}
              />
            )}
          </Group>
        );
      })}
    </Group>
  );
}

// ── Transition keyframes ────────────────────────────────────────

const fadeKeyframes = `
@keyframes dwhFadeIn {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}`;

// ── Step header ─────────────────────────────────────────────────

function StepHeader({
  icon,
  color,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  color: string;
  title: string;
  subtitle: string;
}) {
  return (
    <Group gap="sm" mb="lg">
      <ThemeIcon size={40} radius="xl" variant="light" color={color}>
        {icon}
      </ThemeIcon>
      <Stack gap={0}>
        <Text fw={700} size="lg">{title}</Text>
        <Text size="sm" c="dimmed">{subtitle}</Text>
      </Stack>
    </Group>
  );
}

// ── DwhSetupContent (embeddable) ────────────────────────────────

export function DwhSetupContent({
  onReady,
  contextLabel,
  onClose,
  frame,
}: DwhSetupContentProps) {
  const { startTracking, provisioningStatus } = useDwhProvisioning();
  const { branding } = useBranding();

  const [status, setStatus] = useState<DwhStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<SetupStep>("method");
  const [choice, setChoice] = useState<DwhChoice>(null);

  // Action flags
  const [provisioning, setProvisioning] = useState(false);
  const [connecting, setConnecting] = useState(false);

  // Managed fields
  const [projectName, setProjectName] = useState("");
  const [nameInitialized, setNameInitialized] = useState(false);
  const [customizeName, setCustomizeName] = useState(false);

  // BYO fields
  const [byoProjectId, setByoProjectId] = useState("");
  const [byoLocation, setByoLocation] = useState<string>("EU");
  const [managedLocation, setManagedLocation] = useState<string>("EU");
  const [regions, setRegions] = useState<DwhRegions | null>(null);

  // Validation state (BYO)
  const [validating, setValidating] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [validationAttempted, setValidationAttempted] = useState(false);

  // BYO-Snowflake fields + state (ADR-0175 fase 2)
  const [sfValues, setSfValues] =
    useState<SnowflakeConnectFormValues>(EMPTY_SNOWFLAKE_FORM);
  const [sfAdvancedOpen, setSfAdvancedOpen] = useState(false);
  const [sfStatus, setSfStatus] = useState<SnowflakeDwhStatus | null>(null);
  const [sfConnectResult, setSfConnectResult] =
    useState<SnowflakeDwhConnectResult | null>(null);
  const [sfFetchingScript, setSfFetchingScript] = useState(false);

  // Success state
  const [showSuccess, setShowSuccess] = useState(false);
  const [successProjectId, setSuccessProjectId] = useState<string | null>(null);
  // Non-blocking: the warehouse works, but the service account holds more than
  // BigQuery User on the project (ADR-0328). Shown on the success screen.
  const [successWarnings, setSuccessWarnings] = useState<string[]>([]);
  const [successRegion, setSuccessRegion] = useState<string | null>(null);

  // Stable callback ref to avoid re-triggering effects
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  // ── Initial fetch ──

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getDwhStatus()
      .then(async (data) => {
        if (cancelled) return;
        setStatus(data);
        if (data.provisioning_status === "ACTIVE") {
          onReadyRef.current?.();
        } else if (data.provisioning_status === "PROVISIONING") {
          setStep("connect");
        } else if (data.provisioning_status === "PENDING_VALIDATION") {
          // `dwh_provisioning_status` is one shared column, so it cannot tell a
          // half-finished BigQuery connect from a half-finished Snowflake one.
          // Ask the Snowflake endpoint which warehouse this account is on.
          // A failure (403 for a non-admin, network) degrades to the BigQuery
          // branch — the only behaviour that existed before.
          const sf = await getSnowflakeDwhStatus().catch(() => null);
          if (cancelled) return;
          if (sf?.warehouse_kind === "snowflake") {
            setSfStatus(sf);
            setSfValues({
              account: sf.snowflake_account ?? "",
              database: sf.snowflake_database ?? "",
              warehouse: sf.snowflake_warehouse ?? "",
              role: sf.snowflake_role ?? "",
              user: sf.snowflake_user ?? "",
              privateKeyPem: "",
            });
            setChoice("snowflake");
          } else {
            setChoice("byo");
          }
          setStep("connect");
        } else if (data.provisioning_status === "FAILED") {
          setStep("connect");
        }
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.message ?? "Failed to load DWH status");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Initialize project name from status ──

  useEffect(() => {
    if (nameInitialized || !status) return;
    if (status.provisioning_status === "FAILED" && status.project_id) {
      setProjectName(stripSuffix(status.project_id, status.project_suffix));
    } else if (status.suggested_project_name) {
      setProjectName(status.suggested_project_name);
    }
    setNameInitialized(true);
  }, [status, nameInitialized]);

  // Load the curated region list for the location pickers (managed + BYO).
  useEffect(() => {
    getDwhRegions()
      .then(setRegions)
      .catch(() => setRegions(null));
  }, []);

  // ── Watch context for provisioning → done transition ──

  useEffect(() => {
    if (status?.provisioning_status !== "PROVISIONING") return;
    if (!provisioningStatus || provisioningStatus === "PROVISIONING") return;

    getDwhStatus().then((data) => {
      setStatus(data);
      if (data.provisioning_status === "ACTIVE") {
        setSuccessProjectId(data.project_id);
        setShowSuccess(true);
      }
    });
  }, [provisioningStatus, status?.provisioning_status]);

  // ── Derived ──

  // Naming contract from the backend (source of truth), with fallbacks until
  // status loads so the UI never hardcodes an environment-specific suffix.
  const projectSuffix = status?.project_suffix ?? DEFAULT_PROJECT_SUFFIX;
  const maxProjectNameLength =
    status?.max_project_name_length ?? DEFAULT_MAX_PROJECT_NAME_LENGTH;

  const nameError = useMemo(
    () => (projectName ? validateProjectName(projectName, maxProjectNameLength) : null),
    [projectName, maxProjectNameLength],
  );

  const fullProjectId = projectName ? `${projectName}${projectSuffix}` : null;

  // Stepper subtitles — show contextual info under completed steps
  const stepSubtitles = useMemo<Partial<Record<SetupStep, string | null>>>(
    () => ({
      method: choice ? CHOICE_LABELS[choice].replace(/\bVerity\b/g, branding.productName) : null,
      setup:
        status?.project_id ||
        (choice === "byo" && byoProjectId ? byoProjectId : null) ||
        (choice === "snowflake" && sfValues.database ? sfValues.database : null),
    }),
    [choice, status?.project_id, byoProjectId, sfValues.database, branding.productName],
  );

  // Snowflake field errors — mirrors of the BFF's format gates so an obvious
  // typo never costs a round trip. The server stays the authority.
  const sfErrors = useMemo(() => snowflakeFormErrors(sfValues), [sfValues]);
  const sfFormValid = Object.keys(sfErrors).length === 0;

  // ── Handlers ──

  const handleSelectChoice = useCallback((c: DwhChoice) => {
    setChoice(c);
    setStep("setup");
    setError(null);
  }, []);

  // `status` is a snapshot from when the modal opened. A retry that already
  // went through, or another tab, may have moved the account on since; this
  // shows where it really is. Returns true when there is nothing left to do.
  const applyFreshStatus = useCallback(
    (data: DwhStatus): boolean => {
      setStatus(data);
      if (data.provisioning_status === "ACTIVE") {
        setError(null);
        setSuccessProjectId(data.project_id);
        setShowSuccess(true);
        return true;
      }
      if (data.provisioning_status === "PROVISIONING") {
        setError(null);
        setStep("connect");
        startTracking();
        return true;
      }
      return false;
    },
    [startTracking],
  );

  const handleProvision = useCallback(
    async (location: string) => {
      setProvisioning(true);
      setError(null);
      try {
        // Don't post a click made on a stale screen.
        const before = await getDwhStatus().catch(() => null);
        if (before && applyFreshStatus(before)) return;

        await provisionDwh({
          bq_location: location,
          ...(projectName && { custom_project_name: projectName }),
        });
        const data = await getDwhStatus();
        setStatus(data);
        setStep("connect");
        startTracking();
      } catch (e: unknown) {
        // The refusal may just mean the account moved on (409: already
        // active / already running). Show where it is now, not the old error.
        const after = await getDwhStatus().catch(() => null);
        if (after && applyFreshStatus(after)) return;
        setError(provisionErrorMessage(e));
      } finally {
        setProvisioning(false);
      }
    },
    [projectName, startTracking, applyFreshStatus],
  );

  // BYO step 2 → step 3: client-side validation only, move to connect step
  const handleByoNext = useCallback(() => {
    if (!byoProjectId.trim()) {
      setError("Please enter a BigQuery Project ID");
      return;
    }
    // Basic GCP project ID format check
    if (!/^[a-z][a-z0-9-]{4,28}[a-z0-9]$/.test(byoProjectId.trim())) {
      setError(
        "Invalid project ID format. Must be 6-30 characters, start with a letter, " +
        "and contain only lowercase letters, digits, and hyphens.",
      );
      return;
    }
    setError(null);
    setStep("connect");
  }, [byoProjectId]);

  // BYO step 3: create SA via /connect API (called on mount of connect step)
  const handleByoConnect = useCallback(async () => {
    setConnecting(true);
    setError(null);
    try {
      await connectDwh({
        bq_project_id: byoProjectId.trim(),
        bq_location: byoLocation,
      });
      const data = await getDwhStatus();
      setStatus(data);
      if (data.provisioning_status === "ACTIVE") {
        setSuccessProjectId(data.project_id);
        setShowSuccess(true);
      }
    } catch (e: any) {
      setError(e?.message ?? "Failed to connect warehouse");
    } finally {
      setConnecting(false);
    }
  }, [byoProjectId, byoLocation]);

  // ── BYO-Snowflake handlers ──

  const handleSnowflakeChange = useCallback(
    (patch: Partial<SnowflakeConnectFormValues>) => {
      setSfValues((prev) => ({ ...prev, ...patch }));
    },
    [],
  );

  /**
   * Step 2 → step 3. Unlike the BigQuery branch (which advances first and
   * connects on arrival), Snowflake connects BEFORE advancing: the grant script
   * IS the content of step 3, so there is nothing to show until the POST
   * succeeded — and a rejected address belongs next to the field that holds it.
   */
  const handleSnowflakeConnect = useCallback(async () => {
    const errors = snowflakeFormErrors(sfValues);
    if (Object.keys(errors).length > 0) {
      setError("Please correct the highlighted fields.");
      return;
    }
    setConnecting(true);
    setError(null);
    try {
      const result = await connectSnowflakeDwh(
        buildSnowflakeConnectPayload(sfValues),
      );
      // The connect response already carries the new provisioning status —
      // re-GETting the BigQuery status endpoint to learn it would be a wasted
      // round trip AND would make the Snowflake branch depend on the other
      // warehouse's route for its own state.
      setSfConnectResult(result);
      setSfStatus(result);
      setValidationErrors([]);
      setValidationAttempted(false);
      setStep("connect");
    } catch (e: any) {
      setError(e?.message ?? "Failed to connect warehouse");
    } finally {
      setConnecting(false);
    }
  }, [sfValues]);

  /**
   * `grant_script` comes back from the connect POST only — the status GET never
   * carries it. Re-showing it therefore means re-POSTing connect, which is
   * idempotent and reuses the stored keypair, so a script the DBA already ran
   * stays valid.
   */
  const handleShowGrantScript = useCallback(async () => {
    setSfFetchingScript(true);
    setError(null);
    try {
      const result = await connectSnowflakeDwh(
        buildSnowflakeConnectPayload(sfValues),
      );
      setSfConnectResult(result);
      setSfStatus(result);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load the grant script");
    } finally {
      setSfFetchingScript(false);
    }
  }, [sfValues]);

  const handleSnowflakeValidate = useCallback(async () => {
    setValidating(true);
    setValidationErrors([]);
    try {
      const result = await validateSnowflakeDwh();
      if (result.provisioning_status === "ACTIVE") {
        setSfStatus((prev) =>
          prev ? { ...prev, provisioning_status: "ACTIVE" } : prev,
        );
        setSuccessProjectId(
          [sfStatus?.snowflake_account, sfStatus?.snowflake_database]
            .filter(Boolean)
            .join(" / ") || null,
        );
        setSuccessRegion(result.region ?? null);
        setShowSuccess(true);
      } else {
        // Customer-ready, exact-named messages — rendered verbatim.
        setValidationErrors(result.validation_errors);
      }
    } catch (e: any) {
      setValidationErrors([e?.message ?? "Validation failed"]);
    } finally {
      setValidationAttempted(true);
      setValidating(false);
    }
  }, [sfStatus?.snowflake_account, sfStatus?.snowflake_database]);

  const handleValidate = useCallback(async () => {
    setValidating(true);
    setValidationErrors([]);
    try {
      const result = await validateDwh();
      if (result.provisioning_status === "ACTIVE") {
        const data = await getDwhStatus();
        setStatus(data);
        setSuccessProjectId(data.project_id);
        setSuccessWarnings(result.validation_warnings ?? []);
        setShowSuccess(true);
      } else {
        setValidationErrors(result.validation_errors);
      }
    } catch (e: any) {
      setValidationErrors([e?.message ?? "Validation failed"]);
    } finally {
      setValidating(false);
    }
  }, []);

  const handleContinue = useCallback(() => {
    onReady?.();
  }, [onReady]);

  const handleBack = useCallback(() => {
    const idx = STEP_ORDER.indexOf(step);
    if (idx > 0) {
      setStep(STEP_ORDER[idx - 1]);
      setError(null);
    }
  }, [step]);

  // ── BYO: trigger /connect when entering step 3 ──

  useEffect(() => {
    if (
      step === "connect" &&
      choice === "byo" &&
      status?.provisioning_status !== "PENDING_VALIDATION" &&
      status?.provisioning_status !== "ACTIVE" &&
      !connecting
    ) {
      handleByoConnect();
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Render: loading ──

  if (loading) {
    const checking = (
      <Stack align="center" py="xl" gap="md">
        <Loader size="md" />
        <Text size="sm" c="dimmed">
          Checking data warehouse status...
        </Text>
      </Stack>
    );
    return frame ? frame({ stepper: null, body: checking, footer: null }) : checking;
  }

  // On the Snowflake branch the Snowflake endpoints are the source of truth for
  // the shared provisioning status: connect returns it directly, so the wizard
  // never has to ask the BigQuery route what the Snowflake route just did.
  const provStatus =
    choice === "snowflake"
      ? sfStatus?.provisioning_status ?? status?.provisioning_status
      : status?.provisioning_status;
  const subtitle = contextLabel
    ? `Before connecting ${contextLabel}, you need a data warehouse to store your data.`
    : "Configure the BigQuery project where your data pipelines store data.";

  // ── Render ──

  // The three regions are built once. A caller that passes `frame` lays them
  // out itself — DwhSetupModal hands them to AppModal (stepper pinned under the
  // title, body scrolling, footer pinned). Without one, the self-contained
  // layout below is used: embedded bare (onboarding, tests).
  const stepper = !showSuccess ? (
    <StepIndicator step={step} success={false} subtitles={stepSubtitles} />
  ) : (
    <StepIndicator step="connect" success subtitles={stepSubtitles} />
  );

  const body = (
    <>
      <style dangerouslySetInnerHTML={{ __html: fadeKeyframes }} />
      <Box
        key={`${step}-${showSuccess}`}
        px={frame ? undefined : "xl"}
        py={frame ? undefined : "lg"}
        style={
          frame
            ? { animation: "dwhFadeIn 200ms ease-out" }
            : {
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                animation: "dwhFadeIn 200ms ease-out",
              }
        }
      >
        {/* ── Success ── */}
        {showSuccess && (
          <Stack align="center" py="lg" gap="lg">
            <ThemeIcon size={64} radius="xl" variant="light" color="green">
              <IconCheck size={32} />
            </ThemeIcon>
            <Stack gap={4} align="center">
              <Text fw={700} size="lg">
                Data warehouse ready!
              </Text>
              {successProjectId && (
                <Text size="sm" ff="monospace" c="dimmed">
                  {successProjectId}
                </Text>
              )}
              {successRegion && (
                <Text size="xs" c="dimmed">
                  Region: {successRegion}
                </Text>
              )}
            </Stack>
            {successWarnings.map((warning, i) => (
              <Alert
                key={i}
                color="yellow"
                variant="light"
                title="More access than needed"
                maw={480}
              >
                <Text size="sm">{warning}</Text>
              </Alert>
            ))}
            {contextLabel && (
              <Text size="sm" c="dimmed" ta="center" maw={400}>
                Next: Connect {contextLabel} &rarr; Select data &rarr; Launch
                pipeline
              </Text>
            )}
            <Button onClick={handleContinue} size="md">
              {contextLabel ? `Continue to ${contextLabel} setup` : "Done"}
            </Button>
          </Stack>
        )}

        {/* ── Step 1: Method ── */}
        {!showSuccess && step === "method" && (
          <Stack gap="lg">
            <StepHeader
              icon={<IconDatabase size={22} />}
              color="blue"
              title="Choose your method"
              subtitle="Select how you want to store your managed pipeline data."
            />

            {error && (
              <Alert color="red" icon={<IconAlertCircle size={16} />}>
                {error}
              </Alert>
            )}

            <Grid gutter="md">
              <Grid.Col span={4}>
                <UnstyledButton
                  w="100%"
                  onClick={() => handleSelectChoice("managed")}
                >
                  <Card
                    padding="lg"
                    radius="md"
                    withBorder
                    h="100%"
                    style={{
                      cursor: "pointer",
                      transition: "border-color 150ms",
                    }}
                  >
                    <Stack gap="md" align="center">
                      <ThemeIcon
                        size={48}
                        radius="xl"
                        variant="light"
                        color="blue"
                      >
                        <IconCloud size={24} />
                      </ThemeIcon>
                      <Text fw={700} size="md" ta="center">
                        {branding.productName} Managed
                      </Text>
                      <Text size="xs" c="dimmed" ta="center">
                        We create and manage a dedicated BigQuery project for
                        you. No configuration needed — takes about 2 minutes.
                      </Text>
                    </Stack>
                  </Card>
                </UnstyledButton>
              </Grid.Col>

              <Grid.Col span={4}>
                <UnstyledButton
                  w="100%"
                  onClick={() => handleSelectChoice("byo")}
                >
                  <Card
                    padding="lg"
                    radius="md"
                    withBorder
                    h="100%"
                    style={{
                      cursor: "pointer",
                      transition: "border-color 150ms",
                    }}
                  >
                    <Stack gap="md" align="center">
                      <ThemeIcon
                        size={48}
                        radius="xl"
                        variant="light"
                        color="gray"
                      >
                        <IconServer size={24} />
                      </ThemeIcon>
                      <Text fw={700} size="md" ta="center">
                        Your own BigQuery
                      </Text>
                      <Text size="xs" c="dimmed" ta="center">
                        Connect your existing BigQuery project. You manage
                        the infrastructure, we handle the data pipelines.
                      </Text>
                    </Stack>
                  </Card>
                </UnstyledButton>
              </Grid.Col>

              <Grid.Col span={4}>
                <UnstyledButton
                  w="100%"
                  onClick={() => handleSelectChoice("snowflake")}
                >
                  <Card
                    padding="lg"
                    radius="md"
                    withBorder
                    h="100%"
                    style={{
                      cursor: "pointer",
                      transition: "border-color 150ms",
                    }}
                  >
                    <Stack gap="md" align="center">
                      <ThemeIcon
                        size={48}
                        radius="xl"
                        variant="light"
                        color="cyan"
                      >
                        <IconSnowflake size={24} />
                      </ThemeIcon>
                      <Text fw={700} size="md" ta="center">
                        Your own Snowflake
                      </Text>
                      <Text size="xs" c="dimmed" ta="center">
                        Connect your existing Snowflake account. Your
                        administrator runs one grant script, we do the rest.
                      </Text>
                    </Stack>
                  </Card>
                </UnstyledButton>
              </Grid.Col>
            </Grid>
          </Stack>
        )}

        {/* ── Step 2: Setup (managed) ── */}
        {!showSuccess && step === "setup" && choice === "managed" && (
          <Stack gap="md">
            <StepHeader
              icon={<IconCloud size={22} />}
              color="blue"
              title={`${branding.productName} Managed`}
              subtitle={`We'll create a dedicated BigQuery project, fully managed by ${branding.productName}.`}
            />

            {error && (
              <Alert color="red" icon={<IconAlertCircle size={16} />}>
                {error}
              </Alert>
            )}

            {!customizeName ? (
              <Group gap="xs">
                <Text size="sm" c="dimmed">
                  Project:{" "}
                  <Text span ff="monospace" size="sm">
                    {fullProjectId || "..."}
                  </Text>
                </Text>
                <UnstyledButton onClick={() => setCustomizeName(true)}>
                  <Text size="xs" c="blue" td="underline">
                    Customize
                  </Text>
                </UnstyledButton>
              </Group>
            ) : (
              <Stack gap={4}>
                <TextInput
                  label="Project name"
                  value={projectName}
                  onChange={(e) =>
                    setProjectName(e.currentTarget.value.toLowerCase())
                  }
                  error={nameError}
                  placeholder="my-company"
                  rightSection={
                    <Text
                      size="xs"
                      c="dimmed"
                      ff="monospace"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {projectSuffix}
                    </Text>
                  }
                  rightSectionWidth={projectSuffix.length * 7 + 16}
                />
                {fullProjectId && !nameError && (
                  <Text size="xs" c="dimmed" ff="monospace">
                    {fullProjectId}
                  </Text>
                )}
              </Stack>
            )}

            <Select
              label="Data region"
              description="Where your data warehouse lives. You can change this later, before adding data sources."
              data={regionsToSelectData(regions)}
              value={managedLocation}
              onChange={(v) => setManagedLocation(v || "EU")}
              searchable
              w={280}
            />
          </Stack>
        )}

        {/* ── Step 2: Setup (BYO) ── */}
        {!showSuccess && step === "setup" && choice === "byo" && (
          <Stack gap="md">
            <StepHeader
              icon={<IconServer size={22} />}
              color="gray"
              title="Bring Your Own"
              subtitle="Connect your existing BigQuery project. We'll validate write access after connecting."
            />

            {error && (
              <Alert color="red" icon={<IconAlertCircle size={16} />}>
                {error}
              </Alert>
            )}

            <TextInput
              label="BigQuery Project ID"
              placeholder="my-gcp-project"
              value={byoProjectId}
              onChange={(e) => setByoProjectId(e.currentTarget.value)}
              required
            />
            <Select
              label="Warehouse Location"
              description="Pick the region your data is in — we'll confirm it when you validate access."
              data={regionsToSelectData(regions)}
              value={byoLocation}
              onChange={(v) => setByoLocation(v || "EU")}
              searchable
            />
          </Stack>
        )}

        {/* ── Step 2: Setup (BYO Snowflake) ── */}
        {!showSuccess && step === "setup" && choice === "snowflake" && (
          <Stack gap="md">
            <StepHeader
              icon={<IconSnowflake size={22} />}
              color="cyan"
              title="Your own Snowflake"
              subtitle="Tell us where your Snowflake lives. We'll generate a grant script for your administrator."
            />

            {error && (
              <Alert color="red" icon={<IconAlertCircle size={16} />}>
                {error}
              </Alert>
            )}

            <SnowflakeSetupForm
              values={sfValues}
              onChange={handleSnowflakeChange}
              errors={sfErrors}
              advancedOpen={sfAdvancedOpen}
              onToggleAdvanced={() => setSfAdvancedOpen((o) => !o)}
            />
          </Stack>
        )}

        {/* ── Step 3: Connect — BYO Snowflake ── */}
        {!showSuccess &&
          step === "connect" &&
          choice === "snowflake" &&
          provStatus !== "FAILED" && (
          <Stack gap="lg">
            {connecting ? (
              <>
                <StepHeader
                  icon={<IconSnowflake size={22} />}
                  color="cyan"
                  title="Preparing your connection..."
                  subtitle="Creating the key pair and your grant script. This may take a moment."
                />
                <Stack align="center" py="lg">
                  <Loader size="lg" />
                </Stack>
              </>
            ) : (
              <>
                <StepHeader
                  icon={<IconSnowflake size={22} />}
                  color="cyan"
                  title="Grant access in Snowflake"
                  subtitle={`Run the script below in Snowflake, then let ${branding.productName} validate the connection.`}
                />

                {error && (
                  <Alert color="red" icon={<IconAlertCircle size={16} />}>
                    {error}
                  </Alert>
                )}

                <SnowflakeConnectInstructions
                  connectResult={sfConnectResult}
                  status={sfStatus}
                  productName={branding.productName}
                  onShowGrantScript={handleShowGrantScript}
                  showingGrantScript={sfFetchingScript}
                  validationErrors={validationErrors}
                  validated={validationAttempted}
                />
              </>
            )}
          </Stack>
        )}

        {/* ── Step 3: Connect — Provisioning ── */}
        {!showSuccess && step === "connect" && provStatus === "PROVISIONING" && (
          <Stack gap="lg">
            <StepHeader
              icon={<IconCloud size={22} />}
              color="blue"
              title="Setting up your data warehouse..."
              subtitle="This usually takes 1-2 minutes. The wizard will continue automatically once ready."
            />
            <Stack align="center" py="lg" gap="md">
              <Loader size="lg" />
              {status?.project_id && (
                <Text size="sm" ff="monospace" c="dimmed">
                  {status.project_id}
                </Text>
              )}
            </Stack>
          </Stack>
        )}

        {/* ── Step 3: Connect — BYO (connecting + pending validation) ── */}
        {!showSuccess &&
          step === "connect" &&
          choice === "byo" &&
          provStatus !== "FAILED" && (
            <Stack gap="lg">
              {connecting ? (
                <>
                  <StepHeader
                    icon={<IconPlugConnected size={22} />}
                    color="blue"
                    title="Connecting your project..."
                    subtitle="Setting up the service account. This may take a moment."
                  />
                  <Stack align="center" py="lg">
                    <Loader size="lg" />
                  </Stack>
                </>
              ) : (
                <>
                  <StepHeader
                    icon={<IconPlugConnected size={22} />}
                    color="blue"
                    title="Connect your project"
                    subtitle={`Follow these steps to grant ${branding.productName} access to your BigQuery project.`}
                  />

              {/* Instruction steps */}
              <Stack gap="md">
                {/* Step 1: Copy SA */}
                <Stack gap={4}>
                  <Text size="sm" fw={600}>
                    1. Copy the service account email
                  </Text>
                    {status?.runtime_sa_email && (
                      <CopyButton value={status.runtime_sa_email}>
                        {({ copied, copy }) => (
                          <UnstyledButton onClick={copy} style={{ width: "100%" }}>
                            <Group
                              gap="xs"
                              px="sm"
                              py={8}
                              wrap="nowrap"
                              style={{
                                borderRadius: "var(--mantine-radius-sm)",
                                backgroundColor: copied
                                  ? "var(--mantine-color-green-0)"
                                  : "var(--mantine-color-gray-0)",
                                border: `1px solid ${copied ? "var(--mantine-color-green-3)" : "var(--mantine-color-gray-3)"}`,
                                transition: "all 150ms",
                              }}
                            >
                              <Text
                                size="sm"
                                ff="monospace"
                                style={{
                                  flex: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {status.runtime_sa_email}
                              </Text>
                              {copied ? (
                                <IconCheck size={14} color="var(--mantine-color-green-6)" />
                              ) : (
                                <IconCopy size={14} color="var(--mantine-color-gray-5)" />
                              )}
                            </Group>
                          </UnstyledButton>
                        )}
                      </CopyButton>
                    )}
                </Stack>

                {/* Step 2: Grant roles */}
                <Stack gap={4}>
                  <Text size="sm" fw={600}>
                    2. Grant this role in Google Cloud Console
                  </Text>
                    <Text size="sm" c="dimmed">
                      Go to <Text span fw={500}>IAM & Admin → IAM</Text> for
                      project{" "}
                      <Text span ff="monospace" fw={500}>
                        {status?.project_id}
                      </Text>{" "}
                      <Badge variant="light" size="xs">{status?.bq_location}</Badge> and
                      add the service account with:
                    </Text>
                    <List size="sm" mt={4}>
                      <List.Item><Text span size="sm" fw={500}>BigQuery User</Text></List.Item>
                    </List>
                    <Text size="sm" c="dimmed" mt={4}>
                      {branding.productName} only writes to datasets it creates
                      itself. If it should read datasets in this project (for
                      example a GA4 export), grant{" "}
                      <Text span fw={500}>BigQuery Data Viewer</Text> on those
                      datasets.
                    </Text>
                    <Text size="xs" c="dimmed" mt={4}>
                      Tip: use a dedicated Google Cloud project for{" "}
                      {branding.productName} to keep its data separate from
                      your other work.
                    </Text>
                </Stack>

                {/* Step 3: Validate */}
                <Stack gap={4}>
                  <Text size="sm" fw={600}>
                    3. Validate access
                  </Text>
                  <Text size="sm" c="dimmed">
                    Click the button below to verify the connection.
                  </Text>
                </Stack>
              </Stack>

              {validationErrors.length > 0 && (
                <Stack gap="xs">
                  {validationErrors.map((err, i) => (
                    <Alert
                      key={i}
                      color="red"
                      icon={<IconAlertCircle size={16} />}
                    >
                      {err}
                    </Alert>
                  ))}
                </Stack>
              )}
                </>
              )}
            </Stack>
          )}

        {/* ── Step 3: Connect — Failed ── */}
        {!showSuccess && step === "connect" && provStatus === "FAILED" && (
          <Stack gap="lg">
            <StepHeader
              icon={<IconAlertCircle size={22} />}
              color="red"
              title="Setup failed"
              // The answer to the last click beats the snapshot the modal
              // opened with: a stale screen kept showing an old 403 while the
              // retry's real answer was set and never rendered (2026-09-23).
              subtitle={
                error ||
                status?.error_message ||
                "An unexpected error occurred during provisioning."
              }
            />
          </Stack>
        )}
      </Box>
    </>
  );

  const footer = !showSuccess && step !== "method" ? (
    <Group justify="space-between">
      <Button
        variant="subtle"
        color="gray"
        leftSection={<IconArrowLeft size={16} />}
        onClick={handleBack}
        disabled={provStatus === "PROVISIONING"}
      >
        Back
      </Button>

      {/* Step 2: managed — region buttons */}
      {step === "setup" && choice === "managed" && (
        <Button
          onClick={() => handleProvision(managedLocation)}
          loading={provisioning}
          disabled={
            (customizeName && (!!nameError || !projectName)) || connecting
          }
        >
          Set up data warehouse
        </Button>
      )}

      {/* Step 2: BYO — next button */}
      {step === "setup" && choice === "byo" && (
        <Button
          onClick={handleByoNext}
          disabled={!byoProjectId.trim()}
        >
          Continue
        </Button>
      )}

      {/* Step 2: BYO Snowflake — connect, then show the grant script */}
      {step === "setup" && choice === "snowflake" && (
        <Button
          onClick={handleSnowflakeConnect}
          loading={connecting}
          disabled={!sfFormValid}
        >
          Continue
        </Button>
      )}

      {/* Step 3: pending validation (BYO — BigQuery or Snowflake) */}
      {step === "connect" && provStatus === "PENDING_VALIDATION" && !connecting && (
        <Button
          onClick={
            choice === "snowflake" ? handleSnowflakeValidate : handleValidate
          }
          loading={validating}
          leftSection={<IconCheck size={16} />}
        >
          Validate access
        </Button>
      )}

      {/* Step 3: failed — retry */}
      {step === "connect" && provStatus === "FAILED" && (
        <Button
          leftSection={<IconCloud size={16} />}
          onClick={() =>
            handleProvision(status?.bq_location || "EU")
          }
          loading={provisioning}
        >
          Retry provisioning
        </Button>
      )}
    </Group>
  ) : null;

  if (frame) return frame({ stepper, body, footer });

  return (
    <Stack
      gap={0}
      style={{
        minHeight: 460,
        // Cap at the viewport so the footer (Back / Continue / Validate)
        // stays in view on small windows — the CONTENT area below scrolls,
        // the header and footer never leave the screen.
        maxHeight: "calc(100dvh - 6rem)",
        overflow: "hidden",
      }}
    >
      {/* ── Header + Stepper ── */}
      <Box px="xl" pt="lg" pb="md">
        <Group justify="space-between" align="center" mb="md">
          <Text fw={700} size="lg">
            Set up data warehouse
          </Text>
          {onClose && (
            <UnstyledButton onClick={onClose} aria-label="Close">
              <Text size="xl" c="dimmed" lh={1}>
                &times;
              </Text>
            </UnstyledButton>
          )}
        </Group>

        {stepper}
      </Box>

      {/* ── Divider (edge-to-edge) ── */}
      <Divider />

      {body}

      {/* ── Footer navigation ── */}
      {footer && (
        <Box
          px="xl"
          py="md"
          style={{
            borderTop:
              "1px solid var(--app-border, var(--mantine-color-gray-2))",
          }}
        >
          {footer}
        </Box>
      )}
    </Stack>
  );
}

function provisionErrorMessage(e: unknown): string {
  if (e instanceof ApiError && e.status === 409) {
    const detail = String(e.detail ?? e.message).toLowerCase();
    if (detail.includes("already has an active")) {
      return "This account already has an active data warehouse.";
    }
    if (detail.includes("in progress")) return "Setup is already running.";
  }
  return e instanceof Error && e.message ? e.message : "Failed to start provisioning";
}

// ── DwhSetupModal (standalone wrapper) ──────────────────────────

export default function DwhSetupModal({
  opened,
  onClose,
  zIndex,
  ...contentProps
}: DwhSetupModalProps) {
  // DwhSetupContent fetches on mount and holds the whole flow's state. Inside
  // a Mantine Modal it mounted per open (a closed Modal renders nothing); now
  // that it renders the AppModal itself, `session` restores that: nothing
  // mounts before the first open, and every open starts a fresh flow.
  // (Adjusted during render, the React-docs pattern for state derived from a
  // prop change — no extra committed render with a stale session.)
  const [session, setSession] = useState(0);
  const [lastOpened, setLastOpened] = useState(false);
  if (opened !== lastOpened) {
    setLastOpened(opened);
    if (opened) setSession((n) => n + 1);
  }

  if (session === 0) return null;

  return (
    <DwhSetupModalSession
      key={session}
      opened={opened}
      onClose={onClose}
      zIndex={zIndex}
      {...contentProps}
    />
  );
}

function DwhSetupModalSession({
  opened,
  onClose,
  zIndex,
  ...contentProps
}: DwhSetupModalProps) {
  // Open one frame after mount, so the modal's enter transition runs: a
  // Mantine Modal that mounts already open skips it (use-transition starts
  // in "entered").
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <DwhSetupContent
      {...contentProps}
      frame={({ stepper, body, footer }) => (
        <AppModal
          opened={opened && shown}
          onClose={onClose}
          title="Set up data warehouse"
          size="lg"
          // The owner of a raised layer (the source wizard, 300) passes it.
          zIndex={zIndex}
          stickyTop={stepper ?? undefined}
          footer={footer ?? undefined}
        >
          {body}
        </AppModal>
      )}
    />
  );
}
